/**
 * Cliente da API (docs/api/README.md), o mesmo do sistema da clínica (`apps/web`) e do portal do
 * paciente (`apps/portal`; decisão T34): monta a URL na base da API, manda o token da sessão,
 * desembrulha `{ data }` e `{ data, meta }` e transforma `{ error }` em `ApiError`. Nenhuma página
 * chama `fetch`: cada área tem um `api.ts` que usa este cliente. TypeScript puro, sem React.
 *
 * Toda chamada aceita cabeçalhos próprios (`{ headers: { 'If-Match': '"6"' } }`, a trava otimista
 * dos planos) e a leitura devolve o `ETag` quando a tela pede (`getConditional`, que também entende
 * o 304 de "nada mudou").
 */

export type ErrorCode =
  | 'bad_request'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation_failed'
  | 'link_expired'
  | 'link_invalid'
  | 'rate_limited'
  | 'not_implemented'
  | 'unavailable'
  | 'internal_error'
  | 'network_error'

/** Erro com causa e caminho, como a API manda. `fields` traz a mensagem de cada campo (422). */
export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode
  readonly fields: Record<string, string>
  readonly requestId: string | null
  /** O que mais veio no corpo do erro: `existing` no 409, `diagnostics` na publicação. */
  readonly details: Record<string, unknown>

  constructor(init: {
    status: number
    code: ErrorCode
    message: string
    fields?: Record<string, string>
    requestId?: string | null
    details?: Record<string, unknown>
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.fields = init.fields ?? {}
    this.requestId = init.requestId ?? null
    this.details = init.details ?? {}
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export interface ListMeta {
  page: number
  perPage: number
  total: number
  totalPages: number
}

export interface ListResult<T, M = ListMeta> {
  data: T[]
  meta: M
}

export type QueryValue = string | number | boolean | null | undefined | readonly string[]
export type Query = Record<string, QueryValue>

/** Um arquivo que a API devolve para baixar: o conteúdo e o nome do `Content-Disposition`. */
export interface DownloadedFile {
  blob: Blob
  fileName: string | null
}

/** O que uma chamada pode levar além do corpo: cabeçalhos próprios e a chave de idempotência. */
export interface RequestOptions {
  /** Cabeçalhos a mais, como `If-Match` (trava otimista) ou `If-None-Match`. */
  headers?: Record<string, string>
  /** `Idempotency-Key` de um `POST` que cria ou de uma ação que muda estado. */
  idempotencyKey?: string
}

/**
 * Uma leitura condicional: ou nada mudou (304, sem corpo) ou o item veio com o `ETag` da resposta
 * (`"6"`, a versão do plano). O `ETag` chega como a API o mandou, com as aspas.
 */
export type ConditionalResult<T> =
  { notModified: true; etag: string | null } | { notModified: false; data: T; etag: string | null }

export interface Api {
  /** `GET` de um item: devolve o `data` do envelope. */
  get<T>(path: string, query?: Query, options?: RequestOptions): Promise<T>
  /**
   * `GET` de um item que pode responder 304 (nada mudou desde `version`/`If-None-Match`): devolve o
   * item com o `ETag`, ou só que nada mudou. É a leitura do polling e da trava otimista.
   */
  getConditional<T>(
    path: string,
    query?: Query,
    options?: RequestOptions,
  ): Promise<ConditionalResult<T>>
  /** `GET` de lista: devolve `data` e `meta` (com `counts` quando a tela tem segmentos). */
  list<T, M = ListMeta>(
    path: string,
    query?: Query,
    options?: RequestOptions,
  ): Promise<ListResult<T, M>>
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  /**
   * `GET` de um arquivo (o CSV de uma exportação): devolve o conteúdo como veio e o nome que a API
   * deu. Quando a API recusa, a resposta é o envelope de erro de sempre e vira `ApiError`.
   */
  download(path: string, query?: Query, options?: RequestOptions): Promise<DownloadedFile>
  /** `DELETE`; o corpo vai quando a remoção pede justificativa (docs/api/admin.md, usuários). */
  delete(path: string, body?: unknown, options?: RequestOptions): Promise<void>
}

export interface ApiOptions {
  /**
   * Base pública da API, sem barra no fim: `https://api.westhetics.io` (T30) ou, sem domínio
   * próprio, `https://<projeto>.supabase.co/functions/v1`.
   */
  baseUrl: string
  anonKey: string
  /** Token da sessão; `null` quando ninguém entrou. */
  getAccessToken: () => Promise<string | null>
  fetch?: typeof fetch
  /** Chamado no 401: a sessão acabou (RN-AUT-19). */
  onUnauthenticated?: () => void
}

function buildUrl(baseUrl: string, path: string, query?: Query) {
  const url = new URL(`${baseUrl.replace(/\/$/, '')}${path}`)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) for (const item of value) url.searchParams.append(key, item)
    else url.searchParams.set(key, String(value))
  }
  return url.toString()
}

const NETWORK_MESSAGE =
  'Não conseguimos falar com o servidor. Confira a conexão e tente de novo; nada foi perdido.'

export function createApi(options: ApiOptions): Api {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)

  async function send(
    method: string,
    path: string,
    init: { query?: Query; body?: unknown } & RequestOptions = {},
  ): Promise<Response> {
    const token = await options.getAccessToken()
    const headers: Record<string, string> = { apikey: options.anonKey }
    if (token) headers.Authorization = `Bearer ${token}`
    if (init.body !== undefined) headers['Content-Type'] = 'application/json'
    if (init.idempotencyKey) headers['Idempotency-Key'] = init.idempotencyKey
    Object.assign(headers, init.headers ?? {})

    try {
      return await doFetch(buildUrl(options.baseUrl, path, init.query), {
        method,
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      })
    } catch {
      throw new ApiError({ status: 0, code: 'network_error', message: NETWORK_MESSAGE })
    }
  }

  async function readJson(response: Response): Promise<unknown> {
    try {
      return await response.json()
    } catch {
      return null
    }
  }

  async function request(
    method: string,
    path: string,
    init: { query?: Query; body?: unknown } & RequestOptions = {},
  ): Promise<unknown> {
    const response = await send(method, path, init)
    if (response.status === 204) return undefined
    const payload = await readJson(response)
    if (!response.ok) throw toApiError(response, payload)
    return payload
  }

  /** O envelope `{ error }` da API como `ApiError`; 401 avisa que a sessão acabou. */
  function toApiError(response: Response, payload: unknown): ApiError {
    const error = (payload as { error?: Record<string, unknown> } | null)?.error ?? {}
    const { code, message, fields, requestId, ...details } = error
    if (response.status === 401) options.onUnauthenticated?.()
    return new ApiError({
      status: response.status,
      code: (typeof code === 'string' ? code : 'internal_error') as ErrorCode,
      message:
        typeof message === 'string'
          ? message
          : 'Algo falhou do nosso lado. Tente de novo em instantes.',
      fields: (fields as Record<string, string> | undefined) ?? {},
      requestId:
        (typeof requestId === 'string' ? requestId : null) ?? response.headers.get('x-request-id'),
      details,
    })
  }

  /** `filename="pacientes-2026-10-04.csv"` (ou `filename*=UTF-8''…`) do `Content-Disposition`. */
  function fileNameOf(response: Response): string | null {
    const header = response.headers.get('content-disposition') ?? ''
    const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)?.[1]
    if (encoded) {
      try {
        return decodeURIComponent(encoded.trim())
      } catch {
        return encoded.trim()
      }
    }
    return /filename="?([^";]+)"?/i.exec(header)?.[1]?.trim() ?? null
  }

  async function download(
    path: string,
    query?: Query,
    opts?: RequestOptions,
  ): Promise<DownloadedFile> {
    const response = await send('GET', path, { query, headers: opts?.headers })
    if (!response.ok) throw toApiError(response, await readJson(response))
    return { blob: await response.blob(), fileName: fileNameOf(response) }
  }

  return {
    async get<T>(path: string, query?: Query, opts?: RequestOptions) {
      const payload = (await request('GET', path, { query, headers: opts?.headers })) as {
        data: T
      }
      return payload.data
    },
    async getConditional<T>(path: string, query?: Query, opts?: RequestOptions) {
      const response = await send('GET', path, { query, headers: opts?.headers })
      const etag = response.headers.get('etag')
      if (response.status === 304) return { notModified: true as const, etag }
      const payload = await readJson(response)
      if (!response.ok) throw toApiError(response, payload)
      return { notModified: false as const, data: (payload as { data: T }).data, etag }
    },
    async list<T, M = ListMeta>(path: string, query?: Query, opts?: RequestOptions) {
      const payload = (await request('GET', path, {
        query,
        headers: opts?.headers,
      })) as ListResult<T, M>
      return { data: payload.data, meta: payload.meta }
    },
    async post<T>(path: string, body?: unknown, opts?: RequestOptions) {
      const payload = (await request('POST', path, {
        body: body ?? {},
        idempotencyKey: opts?.idempotencyKey,
        headers: opts?.headers,
      })) as { data: T } | undefined
      return payload?.data as T
    },
    async put<T>(path: string, body?: unknown, opts?: RequestOptions) {
      const payload = (await request('PUT', path, {
        body: body ?? {},
        idempotencyKey: opts?.idempotencyKey,
        headers: opts?.headers,
      })) as { data: T } | undefined
      return payload?.data as T
    },
    download,
    async delete(path: string, body?: unknown, opts?: RequestOptions) {
      await request('DELETE', path, { body, headers: opts?.headers })
    },
  }
}

/** Chave de idempotência para um `POST` que cria: o app pode repetir sem criar duas vezes. */
export function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

/** `6` → `"6"`: a versão como a API espera no `If-Match` (aspas, como um ETag fraco). */
export function versionTag(version: number | string): string {
  return `"${String(version).replace(/^"|"$/g, '')}"`
}

/** `"6"` → `6`; `null` quando o `ETag` não é uma versão numérica. */
export function versionFromTag(etag: string | null): number | null {
  if (!etag) return null
  const digits = etag.replace(/^W\//, '').replace(/^"|"$/g, '')
  return /^\d+$/.test(digits) ? Number(digits) : null
}
